import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, ModalUI, usePortal } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import './metricsInfoModal.scss';

type ModalHandler = { closeModal: () => void; openModal: () => void };

/** The two explanations the reach & frequency KPIs link to; each one links to the other. */
export type MetricsInfoTopic = 'coverage' | 'reachFrequency';

type Props = {
  id: string;
  /** Topic shown, or null when the modal is closed. */
  topic: MetricsInfoTopic | null;
  onChangeTopic: (topic: MetricsInfoTopic) => void;
  onClose: () => void;
};

type Definition = { term: string; description: string };

/**
 * "How coverage is calculated" / "How reach & frequency work" modal: an illustrative
 * formula and the definitions behind the reach & frequency KPIs.
 */
export default function MetricsInfoModal({ id, topic, onChangeTopic, onClose }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { themeContrast } = useTheme() as { themeContrast: 'light' | 'dark' | 'darkAI' };
  const modalRef = useRef<ModalHandler>(null);
  const isOpen = topic !== null;

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.openModal();
    } else {
      modalRef.current?.closeModal();
    }
  }, [isOpen]);

  const targetReachTerm = formatMessage({
    defaultMessage: 'Target reach',
    id: 'doohReport.metricsInfoModal.targetReach'
  });
  const targetUniverseTerm = formatMessage({
    defaultMessage: 'Target Universe',
    id: 'doohReport.metricsInfoModal.targetUniverse'
  });
  const dataSources: Definition = {
    description: formatMessage({
      defaultMessage: 'Mobile SDK and bidstream signals, matched to screen locations and hourly playouts.',
      id: 'doohReport.metricsInfoModal.dataSourcesDescription'
    }),
    term: formatMessage({ defaultMessage: 'Data sources', id: 'doohReport.metricsInfoModal.dataSources' })
  };

  const isCoverage = topic === 'coverage';
  const title = isCoverage
    ? formatMessage({ defaultMessage: 'How coverage is calculated', id: 'doohReport.metricsInfoModal.coverage.title' })
    : formatMessage({ defaultMessage: 'How reach & frequency work', id: 'doohReport.metricsInfoModal.reach.title' });
  const intro = isCoverage
    ? formatMessage({
        defaultMessage: 'Coverage tells you which share of your Target Universe your campaign actually reached.',
        id: 'doohReport.metricsInfoModal.coverage.intro'
      })
    : formatMessage({
        defaultMessage:
          'Reach tells you how many unique people from your Target Audience saw your ads. Frequency tells you how many times, on average, each of them saw them.',
        id: 'doohReport.metricsInfoModal.reach.intro'
      });
  const definitions: Definition[] = isCoverage
    ? [
        {
          description: formatMessage({
            defaultMessage:
              'Unique people from your Target Audience who saw at least one ad, cumulative over the campaign. Example: 10,000 people.',
            id: 'doohReport.metricsInfoModal.coverage.targetReachDescription'
          }),
          term: targetReachTerm
        },
        {
          description: formatMessage({
            defaultMessage:
              'Total potential Target Audience at the selected universe level. Example: 100,000 women aged 25–44 living in the selected area.',
            id: 'doohReport.metricsInfoModal.coverage.targetUniverseDescription'
          }),
          term: targetUniverseTerm
        },
        {
          description: formatMessage({
            defaultMessage:
              'A coverage of 10% means 1 in 10 people of your Target Universe saw your ads at least once. With the same reach, a narrower universe shows a higher coverage.',
            id: 'doohReport.metricsInfoModal.coverage.meaningDescription'
          }),
          term: formatMessage({ defaultMessage: 'What it means', id: 'doohReport.metricsInfoModal.coverage.meaning' })
        },
        dataSources
      ]
    : [
        {
          description: formatMessage({
            defaultMessage:
              'Unique people from your Target Audience who saw at least one ad. Each person counts once: if someone sees your ad 5 times, they add 1 to reach and 5 to impacts.',
            id: 'doohReport.metricsInfoModal.reach.targetReachDescription'
          }),
          term: targetReachTerm
        },
        {
          description: formatMessage({
            defaultMessage:
              'Average number of times each reached person saw your ads. A higher frequency helps people remember your brand; a very high one means the budget keeps reaching the same people.',
            id: 'doohReport.metricsInfoModal.reach.avgFrequencyDescription'
          }),
          term: formatMessage({
            defaultMessage: 'Average frequency',
            id: 'doohReport.metricsInfoModal.reach.avgFrequency'
          })
        },
        {
          description: formatMessage({
            defaultMessage:
              'Reach only adds new people, so the curve grows fast at the start and flattens as the same people see the ads again, which raises frequency.',
            id: 'doohReport.metricsInfoModal.reach.cumulativeReachDescription'
          }),
          term: formatMessage({
            defaultMessage: 'Cumulative reach',
            id: 'doohReport.metricsInfoModal.reach.cumulativeReach'
          })
        },
        {
          description: formatMessage({
            defaultMessage:
              'Reach and frequency stay the same at every level. Changing the Target Universe level only recalculates Target Universe, On-target and Coverage.',
            id: 'doohReport.metricsInfoModal.reach.universeLevelDescription'
          }),
          term: formatMessage({
            defaultMessage: 'Universe level',
            id: 'doohReport.metricsInfoModal.reach.universeLevel'
          })
        },
        dataSources
      ];

  const formula = isCoverage ? (
    <>
      <span className="metricsInfoModal__formulaTitle">
        {formatMessage(
          { defaultMessage: 'Coverage = {reach} ÷ {universe}', id: 'doohReport.metricsInfoModal.coverage.formula' },
          {
            reach: <span className="metricsInfoModal__term metricsInfoModal__term--reach">{targetReachTerm}</span>,
            universe: (
              <span className="metricsInfoModal__term metricsInfoModal__term--universe">{targetUniverseTerm}</span>
            )
          }
        )}
      </span>
      <span className="metricsInfoModal__formulaExample">
        <span className="metricsInfoModal__term metricsInfoModal__term--reach">10,000</span> ÷{' '}
        <span className="metricsInfoModal__term metricsInfoModal__term--universe">100,000</span> = <strong>10%</strong>
      </span>
    </>
  ) : (
    <>
      <span className="metricsInfoModal__formulaTitle">
        {formatMessage(
          {
            defaultMessage: '{impacts} on your Target Audience ÷ {reach} = Average {frequency}',
            id: 'doohReport.metricsInfoModal.reach.formula'
          },
          {
            frequency: (
              <span className="metricsInfoModal__term metricsInfoModal__term--frequency">
                {formatMessage({ defaultMessage: 'frequency', id: 'doohReport.metricsInfoModal.reach.frequency' })}
              </span>
            ),
            impacts: (
              <span className="metricsInfoModal__term metricsInfoModal__term--impacts">
                {formatMessage({ defaultMessage: 'Impacts', id: 'doohReport.metricsInfoModal.reach.impacts' })}
              </span>
            ),
            reach: <span className="metricsInfoModal__term metricsInfoModal__term--people">{targetReachTerm}</span>
          }
        )}
      </span>
      <span className="metricsInfoModal__formulaExample">
        {formatMessage(
          {
            defaultMessage: 'Example: {impacts} impacts ÷ {people} people = {frequency} impacts / person',
            id: 'doohReport.metricsInfoModal.reach.example'
          },
          {
            frequency: <span className="metricsInfoModal__term metricsInfoModal__term--frequency">5.0</span>,
            impacts: <span className="metricsInfoModal__term metricsInfoModal__term--impacts">50,000</span>,
            people: <span className="metricsInfoModal__term metricsInfoModal__term--people">10,000</span>
          }
        )}
      </span>
    </>
  );

  const modal = ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={themeContrast}>
      <ModalUI
        className="metricsInfoModal"
        closeModalCallback={onClose}
        id={id}
        ref={modalRef}
        title={title}
        width="770px"
      >
        <div className="metricsInfoModal__content">
          <p className="metricsInfoModal__intro">{intro}</p>
          <div className="metricsInfoModal__formula">{formula}</div>
          <dl className="metricsInfoModal__definitions">
            {definitions.map(({ term, description }) => (
              <div className="metricsInfoModal__definition" key={term}>
                <dt className="metricsInfoModal__definitionTerm">{term}</dt>
                <dd className="metricsInfoModal__definitionDescription">{description}</dd>
              </div>
            ))}
          </dl>
          <p className="metricsInfoModal__note">
            {formatMessage({
              defaultMessage:
                'Illustrative example. Your report figures are estimates; some zones may show no data depending on regional activity.',
              id: 'doohReport.metricsInfoModal.note'
            })}
          </p>
          <div className="metricsInfoModal__footer">
            <button
              className="metricsInfoModal__link"
              onClick={() => onChangeTopic(isCoverage ? 'reachFrequency' : 'coverage')}
              type="button"
            >
              {isCoverage
                ? formatMessage({
                    defaultMessage: 'Learn how reach & frequency work',
                    id: 'doohReport.metricsInfoModal.coverage.link'
                  })
                : formatMessage({
                    defaultMessage: 'Learn how coverage is calculated',
                    id: 'doohReport.metricsInfoModal.reach.link'
                  })}
            </button>
            <ButtonUI
              id={`${id}-closeButton`}
              onClick={onClose}
              text={formatMessage({ defaultMessage: 'Close', id: 'doohReport.metricsInfoModal.close' })}
              type="secondary"
            />
          </div>
        </div>
      </ModalUI>
    </CustomThemeProvider>,
    usePortal(id)
  );

  return isOpen ? modal : null;
}
